// Load a publisher's submission as a template for the form below. Minting the
// link that produces those submissions lives above, in
// PublisherIntakeLinkCreator.
//
// Failures here are always local to this card — a broken picker must never
// block onboarding a channel by hand.

import React from 'react';
import { Link2, Loader2, XCircle } from 'lucide-react';
import { publisherIntakeService } from '../../../services/publisherIntakeService';
import type {
  IntakeWarning,
  PublisherIntakeSubmission,
  PublisherIntakeSummary,
} from '../../../types/publisherIntake';
import { inputClass, labelClass } from './fields/publisherFieldStyles';


const WARNING_COPY: Record<IntakeWarning, string> = {
  slug_taken: 'A channel already uses this slug — pick a different one before saving.',
  logo_missing: 'The uploaded logo is no longer in storage. Ask for a new one.',
  clip_missing: 'One or more landing clips are no longer in storage.',
  link_expired: 'This onboarding link has expired (the submission is still usable).',
};

interface Props {
  onSelect: (submission: PublisherIntakeSubmission | null) => void;
  selectedId: string | null;
}

const PublisherIntakeTemplatePicker: React.FC<Props> = ({ onSelect, selectedId }) => {
  const [rows, setRows] = React.useState<PublisherIntakeSummary[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [warnings, setWarnings] = React.useState<IntakeWarning[]>([]);



  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await publisherIntakeService.listSubmissions('submitted');
        if (!cancelled) setRows(list ?? []);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Could not load submissions.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleSelect = async (id: string) => {
    if (!id) {
      setWarnings([]);
      onSelect(null);
      return;
    }
    // Replacing a loaded template discards whatever the admin already typed,
    // because the form remounts.
    if (selectedId && selectedId !== id) {
      const ok = window.confirm(
        'Load a different submission? Anything you have typed in the form below will be replaced.',
      );
      if (!ok) return;
    }
    try {
      const submission = await publisherIntakeService.getSubmission(id);
      setWarnings(submission.warnings ?? []);
      onSelect(submission);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load that submission.');
    }
  };

  return (
    <section
      className="rounded-2xl border border-slate-200 bg-white overflow-hidden"
      style={{ boxShadow: '0 1px 2px rgba(15,23,42,0.08), 0 18px 40px -16px rgba(15,23,42,0.28)' }}
    >
      <header className="flex items-start gap-3 px-5 sm:px-6 py-4 border-b border-slate-200/70">
        <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-slate-100 text-slate-700 shrink-0">
          <Link2 className="w-4 h-4" />
        </div>
        <div className="min-w-0">
          <h3 className="text-[15px] font-semibold tracking-tight text-slate-900">
            Start from a publisher&rsquo;s submission
          </h3>
          <p className="text-[12px] text-slate-500 mt-0.5 leading-relaxed">
            Send a publisher an onboarding link, then load what they filled in to pre-fill
            the form below.
          </p>
        </div>
      </header>

      <div className="px-5 sm:px-6 py-5 space-y-5">
        {error && (
          <div className="flex items-start gap-2.5 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3">
            <XCircle className="w-4 h-4 mt-0.5 text-rose-600 shrink-0" />
            <p className="text-[13px] text-rose-800">{error}</p>
          </div>
        )}

        <div>
          <label className={labelClass} htmlFor="intake-submission">Submitted forms</label>
          {loading ? (
            <p className="flex items-center gap-2 text-[13px] text-slate-500">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading submissions…
            </p>
          ) : rows.length === 0 ? (
            <p className="text-[13px] text-slate-500">
              No publisher submissions yet. Generate a link below to send one.
            </p>
          ) : (
            <select
              id="intake-submission"
              value={selectedId ?? ''}
              onChange={(e) => void handleSelect(e.target.value)}
              className={inputClass}
            >
              <option value="">Start from a blank form…</option>
              {rows.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.stationName ?? r.slug ?? 'Untitled'}
                  {r.organizationName ? ` — ${r.organizationName}` : ''}
                  {r.submittedAt ? ` (submitted ${new Date(r.submittedAt).toLocaleDateString()})` : ''}
                </option>
              ))}
            </select>
          )}
        </div>

        {warnings.length > 0 && (
          <ul className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 space-y-1">
            {warnings.map((w) => (
              <li key={w} className="text-[12px] text-amber-900">{WARNING_COPY[w] ?? w}</li>
            ))}
          </ul>
        )}


      </div>
    </section>
  );
};

export default PublisherIntakeTemplatePicker;
